import { Card, Col, Form, Row } from "react-bootstrap";
import { FaPlus } from "react-icons/fa6";
import { FaTimes } from "react-icons/fa";
import PrimarySmallButton from "../../components/buttons/PrimarySmallButton";
import SecondarySmallButton from "../../components/buttons/SecondarySmallButton";
import { IoSaveOutline } from "react-icons/io5";

import GoogleFullTextIcon from "../../assets/icons/GoogleFullTextIcon";
import { FacebookFullTextIcon } from "../../assets/icons";

import zillowImg from "../../assets/zillow_logo.png";
import HowReviewsWorks from "./components/HowReviewsWorks";
import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useFieldArray, useForm } from "react-hook-form";
import {
  useDeleteReviewLinkMutation,
  useGetReviewLinkQuery,
  useUpdateReviewLinksMutation,
} from "../../features/users/usersApi";
import { ReviewLinkType } from "../../features/users/user.interface";
import { toast } from "react-toastify";

interface PlatformData {
  name: string;
  link?: string;
  type?: ReviewLinkType;
  displayName: string;
  reviewId?: string;
}

const defaultPlatforms: PlatformData[] = [
  {
    name: "Google",
    link: "http://google.com/",
    type: ReviewLinkType.GOOGLE,
    displayName: "Review us on Google",
  },
  {
    name: "Zillow",
    link: "http://zillow.com",
    type: ReviewLinkType.ZILLOW,
    displayName: "Review us on Zillow",
  },
   {
    name: "Facebook",
    link: "http://facebook.com",
    type: ReviewLinkType.FACEBOOK,
    displayName: "Review us on Facebook",
  },
];

const Reviews = () => {
  const [showExample, setShowExample] = useState(false);

  const { data, isLoading, isFetching } = useGetReviewLinkQuery(undefined);

  const [addReviewPlatform] = useUpdateReviewLinksMutation();
  const [deleteReview] = useDeleteReviewLinkMutation();

  const schema = yup
    .object({
      reviewPlatform: yup
        .array()
        .of(
          yup.object({
            name: yup.string().required("Name is required"),
            link: yup.string().optional(),
            displayName: yup.string().required("Display name is required"),
            type: yup.mixed<ReviewLinkType>().optional(),
          })
        )
        .required("Review platform is required")
        .min(1, "At least one review platform is required"),
    })
    .required();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<{ reviewPlatform: PlatformData[] }>({
    resolver: yupResolver(schema),
    defaultValues: {
      reviewPlatform: data?.data
        ? data?.data?.map((review) => ({
          name: review.name,
          link: review.link,
          type: review.type as ReviewLinkType,
          displayName: review.display_name,
          reviewId: review.review_link_id,
        }))
        : defaultPlatforms,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "reviewPlatform",
  });

  const onSubmit = async (data: { reviewPlatform: PlatformData[] }) => {
    const reviews = data.reviewPlatform.map((review) => ({
      name: review.name,
      link: review?.link ?? "",
      type: review.type ?? ReviewLinkType.OTHER,
      display_name: review.displayName,
    }));

    await addReviewPlatform({ reviews }).unwrap();

    toast.success("Review links updated successfully");
  };

  useEffect(() => {
    if (data?.data) {
      reset({
        reviewPlatform: data?.data.map((review) => ({
          name: review.name,
          link: review.link,
          type: review.type as ReviewLinkType,
          displayName: review.display_name,
          reviewId: review.review_link_id,
        })),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data]);

  if (isLoading || isFetching) {
    return <div>Loading...</div>;
  }

  return (

    <Row>
      <Col>
        <Card style={{ border: "none", borderLeft: "1px solid rgba(25, 34, 46, 0.08)", borderRadius: "0", height: "100vh", backgroundColor: "transparent" }}>
          <Card.Body style={{ padding: 0, backgroundColor: "transparent" }}>
            <div
              className="sticky-filter-bar"
              style={{
                position: "sticky",
                top: "50px",
                zIndex: 100,
                height: "50px",
                marginBottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "20px",
                padding: "10px 20px",
                lineHeight: "24px",
                borderBottom: "1px solid #eef0f2",
              }}>
              <p style={{ margin: 0, fontSize: "14px", color: "#6c757d", fontWeight: 600 }}>
                Capture Feedback. Build credibility. Grow Faster
              </p>
              <PrimarySmallButton
                onClick={handleSubmit(onSubmit)}
                disabled={isLoading || isFetching}
                icon={<IoSaveOutline />}
              >
                Save Review Link(s)
              </PrimarySmallButton>
            </div>

            <Row className="m-2 mt-3">
              <Col>
                <Card>
                  <Card.Body>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <Row className="mt-1">
                        <Col lg={12}>
                          <div className="flex justify-between">
                            <div className="d-flex align-items-center gap-1">
                              <div className="settings-header">Review Links</div>(
                              <div className="text-end">
                                <HowReviewsWorks
                                  show={showExample}
                                  onHide={() => setShowExample(!showExample)}
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowExample(!showExample)}
                                  className="font-12 link-text text-end cursor-pointer"
                                >
                                  See How It Works
                                </button>
                              </div>
                              )
                            </div>
                            {fields.length < 5 ? (
                              <SecondarySmallButton
                                onClick={() =>
                                  append({
                                    name: "",
                                    link: "",
                                    displayName: "",
                                    type: ReviewLinkType.OTHER,
                                  })
                                }
                                icon={<FaPlus />}
                              >
                                Add custom link
                              </SecondarySmallButton>
                            ) : (
                              <div />
                            )}
                          </div>
                        </Col>
                      </Row>
                      {fields.map((field, index) => (
                        <Row key={field.id} className="mt-3">
                          <Col md={2} className="flex items-center ">
                            <Form.Group className="w-100">
                              <Form.Label className="flex! items-center gap-1 mb-0">
                                {field.name === "Google" && (
                                  <GoogleFullTextIcon height="24px" width="50px" />
                                )}
                                {field.name === "Zillow" && (
                                  <img
                                    src={zillowImg}
                                    style={{ height: "14px" }}
                                    alt={field.name}
                                  />
                                )}
                                {field.name === "Facebook" && <FacebookFullTextIcon />}
                                {field.type === "other" && (
                                  <span className="text-primary">Plateform Name</span>
                                )}
                              </Form.Label>

                              {!["Google", "Zillow", "Facebook"].includes(field.name) && (
                                <Form.Control
                                  {...register(`reviewPlatform.${index}.name`)}
                                />
                              )}

                              {errors && errors?.reviewPlatform?.[index]?.name ? (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {errors?.reviewPlatform?.[index]?.name?.message}
                                </Form.Control.Feedback>
                              ) : null}
                            </Form.Group>
                          </Col>
                          <Col md={5}>
                            <Form.Group className="w-100 ">
                              <Form.Label className="mb-0">Review Link</Form.Label>
                              <Form.Control {...register(`reviewPlatform.${index}.link`)} />
                              {errors && errors?.reviewPlatform?.[index]?.link ? (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {errors?.reviewPlatform?.[index]?.link?.message}
                                </Form.Control.Feedback>
                              ) : null}
                            </Form.Group>
                          </Col>
                          <Col md={4} className="flex items-end">
                            <Form.Group className="w-100 ">
                              <Form.Label className="whitespace-nowrap mb-0">
                                Display in Chat As:
                              </Form.Label>
                              <div className="grow">
                                <Form.Control
                                  className="grow-1"
                                  {...register(`reviewPlatform.${index}.displayName`)}
                                />
                                {errors && errors?.reviewPlatform?.[index]?.displayName ? (
                                  <Form.Control.Feedback type="invalid" className="d-block">
                                    {errors?.reviewPlatform?.[index]?.displayName?.message}
                                  </Form.Control.Feedback>
                                ) : null}
                              </div>
                            </Form.Group>
                          </Col>
                          <Col md={1} className="flex items-end">
                            {field.type === ReviewLinkType.OTHER && (
                              <button
                                style={{ padding: "5px" }}
                                className="btn btn-sm btn-light d-flex align-items-center"
                                onClick={() => {
                                  if (field?.reviewId) {
                                    deleteReview({ id: field.reviewId });
                                  }
                                  remove(index);
                                }}
                              >
                                <FaTimes color={"grey"} fontSize={12} />
                                Remove
                              </button>
                            )}
                          </Col>
                        </Row>
                      ))}

                    </form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default Reviews;
